import { Component, DestroyRef, OnInit } from '@angular/core';
import { API, FixedAspectRatioImageDTO, ProductFullDTO, SingleFileUpload } from '../../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { ErrorService } from '../../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { Clipping } from '../../../../components/image/model';
import { ImageUploadModel } from '../../../../services/gui/image/model';
import { ImageUploadService } from '../../../../services/gui/image/image-upload.service';
import { NavigationService } from '@ngx-seatback/utils';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-product-details-overview',
  templateUrl: './product-details-overview.component.html',
  styleUrls: ['./product-details-overview.component.scss'],
  standalone: false,
})
export class ProductDetailsOverviewComponent implements OnInit {
  logoImageUpload: ImageUploadModel;
  AmbianceImageUpload: ImageUploadModel;
  productId: number;
  product: ProductFullDTO;
  loading: boolean;
  ambianceUrl: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    private modal: ModalService,
    private imageUploadService: ImageUploadService,
    private errorHandler: ErrorService,
    public translate: TranslateService,
    public util: UtilService,
    private navService: NavigationService,
    private destroyRef: DestroyRef
  ) {
    this.logoImageUpload = new ImageUploadModel();
    this.AmbianceImageUpload = new ImageUploadModel();
    this.productId = undefined;
    this.product = undefined;
    this.loading = true;
    this.ambianceUrl = './assets/img/channel-placeholder.png';
  }

  ngOnInit(): void {
    this.activatedRoute.parent.params.subscribe(params => {
      this.productId = +params.productID;
      this.refreshFromServer();
    });
  }

  refreshFromServer() {
    this.loading = true;
    this.api
      .pm_Product_retrieveById(this.productId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (e: ProductFullDTO) => {
          this.product = e;
          this.ambianceUrl = this.product?.depiction
            ? this.util.appendQueryParamToUrl(this.product.depiction.url as unknown as string, '')
            : './assets/img/channel-placeholder.png';
        },
        error: error => {
          if (error.status && (+error.status === 403 || +error.status === 404)) {
            this.navService.navigateToPoint(this.util.RoutingID.products);
          } else {
            this.errorHandler.errorHandler(error);
          }
        },
      });
  }

  pickAmbiance() {
    this.AmbianceImageUpload?.destroySubscriptions();
    this.api
      .pm_Product_retrieveById(this.productId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (product: ProductFullDTO) => {
          this.AmbianceImageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.ambiance'));
          this.AmbianceImageUpload.imageTransfer.image = product?.depiction;
          this.AmbianceImageUpload.subClippingChange = this.AmbianceImageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.ambianceClippingChange(res);
          });
          this.AmbianceImageUpload.subFileChange = this.AmbianceImageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.ambianceFileChange(file);
          });
          this.AmbianceImageUpload.subDeletePress = this.AmbianceImageUpload.imageTransfer.deletePress.subscribe(() => {
            this.ambianceDeletePress();
          });
          this.AmbianceImageUpload.subModalClosed = this.AmbianceImageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.ambianceUploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceClippingChange(res: Clipping) {
    if (res) {
      this.applyClipping(this.AmbianceImageUpload, res);
      const transImage: FixedAspectRatioImageDTO = this.AmbianceImageUpload.imageTransfer.image;
      this.AmbianceImageUpload.imageTransfer.image = null;
      this.AmbianceImageUpload.imageTransfer.uploading = true;
      this.api
        .pm_Product_clipDepiction(this.productId, transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (image: FixedAspectRatioImageDTO) => {
            this.AmbianceImageUpload.imageTransfer.image = image;
            this.AmbianceImageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  ambianceFileChange(file: SingleFileUpload) {
    this.AmbianceImageUpload.imageTransfer.image = null;
    this.AmbianceImageUpload.imageTransfer.uploading = true;
    this.api
      .pm_Product_uploadDepiction(this.productId, file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: FixedAspectRatioImageDTO) => {
          this.AmbianceImageUpload.imageTransfer.image = res;
          this.AmbianceImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceDeletePress() {
    this.AmbianceImageUpload.imageTransfer.image = null;
    this.AmbianceImageUpload.imageTransfer.uploading = true;
    this.api
      .pm_Product_deleteDepiction(this.productId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.AmbianceImageUpload.imageTransfer.image = null;
          }
          this.AmbianceImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceUploadModalClosed() {
    this.AmbianceImageUpload.destroySubscriptions();
    this.refreshFromServer();
  }

  applyClipping(imageUpload: ImageUploadModel, res: Clipping) {
    imageUpload.imageTransfer.image.clippingTLX = res.clippingTLX;
    imageUpload.imageTransfer.image.clippingTLY = res.clippingTLY;
    imageUpload.imageTransfer.image.clippingBRX = res.clippingBRX;
    imageUpload.imageTransfer.image.clippingBRY = res.clippingBRY;
  }
}
